import React, { FC, memo, useState } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { NOTIFICATION_DEFAULT_ERROR_MESSAGE, useNotificationContext } from 'application/providers/NotificationProvider';

import { usePointUpdateMutation } from '@shared/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';

import { TEXTS } from './texts';

import styles from './index.module.scss';

const PointChangeCommentModal: FC<ModalProps[ModalTypes.POINT_CHANGE_COMMENT]> = ({
  pointId,
  comment: initialComment,
  close,
}) => {
  const notification = useNotificationContext();

  const [mutate, mutationResult] = usePointUpdateMutation({
    refetchQueries: ['point', 'pointList'],
  });

  const [comment, setComment] = useState<string>(initialComment || '');

  const onSave = async () => {
    if (pointId) {
      try {
        const ok = await mutate({ variables: { input: { pointId, comment } } });

        if (ok) {
          close();

          notification.showInfoNotification({
            children: TEXTS.NOTIFICATION_SUCCESS_TEXT,
          });
        } else {
          notification.showErrorNotification({
            children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
          });
        }
      } catch (e: unknown) {
        const error = e as Error;

        if (/value too long/.test(error.message)) {
          notification.showErrorNotification({ children: TEXTS.NOTIFICATION_ERROR_TOO_LONG_TEXT });
        }
      }
    }
  };

  return (
    <ModalScreen
      title={TEXTS.TITLE}
      titlePlacement="left"
      onClose={close}
      className={styles.description}
      actionButtons={[
        <Button
          key="button_change_comment_point_modal"
          elementId="button_change_comment_point_modal"
          type={ButtonType.PRIMARY}
          isLoading={mutationResult.loading}
          isDisabled={comment === initialComment}
          onClick={onSave}>
          {TEXTS.SAVE_BUTTON_TEXT}
        </Button>,
      ]}>
      <Input
        elementId="button_change_comment_input"
        type={InputType.TEXT}
        containerClassName={styles.input}
        value={comment}
        isDisabled={mutationResult.loading}
        placeholder={TEXTS.INPUT_PLACEHOLDER}
        onChange={setComment}
      />
    </ModalScreen>
  );
};

export default memo(PointChangeCommentModal);
